import React, { useState, useMemo, useEffect } from "react";
import {
  LayoutGrid,
  ShieldAlert,
  Users,
  CheckCircle2,
  Circle,
  X,
  FileText,
  ShieldCheck,
  BarChart3,
  Mail,
  Briefcase,
  ExternalLink,
  FileSignature,
  CalendarClock,
  Send,
  Target,
  Search,
  Zap,
  Cpu,
  Database,
  Lock,
  PlayCircle,
  ArrowRight,
  MessageSquare,
  Search as SearchIcon,
  Copy,
  ThumbsUp,
  UserPlus,
  Download,
  UploadCloud,
  ArrowRightCircle,
  Terminal,
  Filter,
  AlertTriangle,
  ChevronRight,
  MoreHorizontal,
  RefreshCw,
  Trash2,
  Menu
} from "lucide-react";

// --- 1. BRAND THEME (SEON.io) ---
const palette = {
  bg: "#F4F7F9",
  bgSidebar: "#0B1221", // Deep Navy
  glass: "rgba(255, 255, 255, 0.95)",
  glassBorder: "rgba(0, 0, 0, 0.06)",
  text: "#0F172A",
  textSoft: "#475569",
  textFaint: "#94A3B8",
  textInverse: "#FFFFFF",
  brand: "#00E3B6", // SEON Bright Mint
  brandDark: "#00A887",
  brandGlow: "rgba(0, 227, 182, 0.15)",
  accent: "#1E293B",
  success: "#10B981",
  warning: "#F59E0B",
  danger: "#EF4444",
  line: "#E2E8F0",
  fairwayDark: "#020617",
  fairwayGreen: "#10B981",
};

// --- RESPONSIVE HOOK ---
const useMediaQuery = (query) => {
  const [matches, setMatches] = useState(false);
  useEffect(() => {
    const media = window.matchMedia(query);
    if (media.matches !== matches) setMatches(media.matches);
    const listener = () => setMatches(media.matches);
    media.addListener(listener);
    return () => media.removeListener(listener);
  }, [matches, query]);
  return matches;
};

const styles = {
  container: {
    display: "flex",
    height: "100vh",
    width: "100vw",
    backgroundColor: palette.bg,
    color: palette.text,
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
    overflow: "hidden",
    position: "relative",
  },
  sidebarDesktop: {
    width: "280px",
    backgroundColor: palette.bgSidebar,
    borderRight: `1px solid ${palette.accent}`,
    display: "flex",
    flexDirection: "column",
    padding: "24px",
    zIndex: 20,
    color: palette.textInverse,
    boxShadow: "4px 0 24px rgba(0,0,0,0.1)",
    height: "100%",
  },
  sidebarMobile: {
    position: "fixed",
    inset: "0 0 0 0",
    backgroundColor: palette.bgSidebar,
    padding: "24px",
    zIndex: 50,
    display: "flex",
    flexDirection: "column",
    width: "85%",
    maxWidth: "320px",
    boxShadow: "10px 0 30px rgba(0,0,0,0.5)",
    transition: "transform 0.3s ease",
  },
  navItem: (active) => ({
    display: "flex",
    alignItems: "center",
    gap: "12px",
    padding: "12px",
    borderRadius: "8px",
    cursor: "pointer",
    backgroundColor: active ? "rgba(255,255,255,0.1)" : "transparent",
    color: active ? palette.brand : palette.textFaint,
    borderLeft: active ? `3px solid ${palette.brand}` : "3px solid transparent",
    transition: "all 0.2s ease",
    fontWeight: active ? 600 : 500,
    fontSize: "14px",
  }),
  glassPanel: (isMobile) => ({
    backgroundColor: palette.glass,
    backdropFilter: "blur(20px)",
    border: `1px solid ${palette.glassBorder}`,
    borderRadius: isMobile ? "0px" : "16px",
    padding: isMobile ? "20px" : "32px",
    height: "100%",
    overflowY: "auto",
    boxShadow: "0 4px 24px rgba(0,0,0,0.04)",
  }),
  card: {
    backgroundColor: "#FFFFFF",
    border: `1px solid ${palette.glassBorder}`,
    borderRadius: "12px",
    padding: "24px",
    boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
    transition: "all 0.2s ease",
    cursor: "pointer",
    position: "relative",
  },
  modalOverlay: {
    position: "fixed",
    inset: 0,
    backgroundColor: "rgba(11, 18, 33, 0.6)",
    backdropFilter: "blur(4px)",
    zIndex: 1000,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "20px",
  },
  modalContent: {
    backgroundColor: "#FFFFFF",
    borderRadius: "16px",
    width: "100%",
    maxWidth: "1100px",
    maxHeight: "90vh",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
  },
  mobileMenuBtn: {
    position: "absolute",
    top: "20px",
    right: "20px",
    zIndex: 30,
    background: "#FFFFFF",
    border: `1px solid ${palette.line}`,
    borderRadius: "8px",
    padding: "8px",
    boxShadow: "0 2px 5px rgba(0,0,0,0.05)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  }
};

// --- 2. HELPERS ---

const Badge = ({ text, tone = "default" }) => {
  const map = {
    default: { bg: "#F1F5F9", text: "#475569" },
    brand: { bg: palette.brandGlow, text: palette.brandDark },
    success: { bg: "#ECFDF5", text: "#047857" },
  };
  const s = map[tone] || map.default;
  return (
    <span style={{ padding: "4px 10px", borderRadius: "99px", fontSize: "11px", fontWeight: "600", backgroundColor: s.bg, color: s.text, textTransform: "uppercase", letterSpacing: "0.05em" }}>
      {text}
    </span>
  );
};

const ProgressBar = ({ total, completed }) => {
  const percentage = total === 0 ? 0 : Math.round((completed / total) * 100);
  return (
    <div style={{ width: "100%", marginTop: "16px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "6px" }}>
        <span style={{ fontSize: "11px", color: palette.textFaint, fontWeight: 600, textTransform: "uppercase" }}>Task Completion</span>
        <span style={{ fontSize: "11px", color: palette.brandDark, fontWeight: 700 }}>{percentage}%</span>
      </div>
      <div style={{ height: "4px", backgroundColor: "#F1F5F9", borderRadius: "2px", overflow: "hidden" }}>
        <div style={{ width: `${percentage}%`, height: "100%", backgroundColor: percentage === 100 ? palette.success : palette.brand, transition: "width 0.5s ease-out" }} />
      </div>
    </div>
  );
};

// --- 3. DATA SOURCES ---

const WEEKS_DATA = [
  {
    id: "WK-JAN05",
    date: "Jan 05, 2026",
    title: "Upcoming: Q1 Decision & Proof Review",
    attendees: "Mike, Ben, Devlyn, Andrea",
    summary: "Post-holiday check-in targeted for the week of the 5th. We need to present the ABX Proof of Performance and finalize the decision on the Q1 lead campaign vertical (likely ATS).",
    tags: ["Closing", "Q1 Planning", "ABX Proof"],
    deliverables: [
      { id: "d_05_1", text: "Build ABX Proof of Performance (Account Plans & Signals)", owner: "Fairway Team", defaultChecked: false },
      { id: "d_05_2", text: "Confirm 'Lead Campaign' vertical selection", owner: "Devlyn (SEON)", defaultChecked: false },
      { id: "d_05_3", text: "Scope out possible Q1 proof of value proposal", owner: "Mike Thyne", defaultChecked: false },
    ],
  },
  {
    id: "WK-DEC17",
    date: "Dec 17, 2025",
    title: "Deep Dive: ICP Priorities & ABX Proposal",
    attendees: "Mike, Ben, Devlyn, Andrea Hilleary",
    summary: "Second meeting. Introduced Andrea (Regional Marketing/NOAM). Discussed the difficulty of getting approved Target Account Lists. Mike proposed an 'ABX Proof of Performance' to demonstrate how Fairway uses financial/social signals to build actionable account plans. **Congrats to Andrea for being promoted to Global Marketing Responsibilities!** We'll have to celebrate since we're in Providence/Hopkinton/Foxboro MA together (Sorry Mike, MN).",
    tags: ["ICP Review", "Proof of Concept", "Strategy"],
    deliverables: [
      { id: "d_17_1", text: "Send Follow-up Recap: 'ABX Proof of Performance' Proposal", owner: "Mike Thyne", defaultChecked: true, links: [{ label: "Read Recap Email", url: "#", action: "OPEN_MIKE_EMAIL" }] },
      { id: "d_17_0", text: "Launch Social Playbook Demo (Reaction Discovery + Message Assist)", owner: "Fairway Team", defaultChecked: true, links: [{ label: "Open Social Playbook", url: "#", action: "OPEN_PLAYBOOK" }] },
      { id: "d_17_2", text: "Review ICP Briefing Doc (received pre-meeting)", owner: "Ben Belcher", defaultChecked: true },
    ],
  },
  {
    id: "WK-DEC12",
    date: "Dec 12, 2025",
    title: "Logistics: MNDA & Scheduling",
    attendees: "Devlyn, Mike, Ben",
    summary: "Operational phase. Devlyn sent over the Mutual NDA via DocuSign and proposed the follow-up meeting time. Introduced Andrea Hilleary as a key stakeholder for NOAM.",
    tags: ["Legal", "Scheduling", "Stakeholder Mapping"],
    deliverables: [
      { id: "d_12_1", text: "Execute SEON MNDA via DocuSign", owner: "Ben Belcher", defaultChecked: true, links: [{ label: "View DocuSign Envelope", url: "#", action: "OPEN_DOCUSIGN" }] },
      { id: "d_12_2", text: "Confirm Dec 17th Slot (4:30 PM)", owner: "Mike Thyne", defaultChecked: true, links: [{ label: "View Scheduling Email", url: "#", action: "OPEN_DEVLYN_EMAIL" }] },
    ],
  },
  {
    id: "WK-DEC02",
    date: "Dec 02, 2025",
    title: "Kickoff: Initial Capabilities Review",
    attendees: "Mike, Ben, Devlyn",
    summary: "Initial introductory call to explore fit. Fairway presented general ABX capabilities. Devlyn expressed interest in operationalizing signals for the sales team.",
    tags: ["Discovery", "Intro", "Fit Check"],
    deliverables: [
      { id: "d_02_1", text: "Complete Intro Call", owner: "All", defaultChecked: true },
      { 
        id: "d_02_2", 
        text: "Send Capabilities Deck", 
        owner: "Ben Belcher", 
        defaultChecked: true,
        links: [
            { label: "Visitor Identification", url: "https://fairwaydigitalmedia.com/visitor-identification" },
            { label: "Developer Intent", url: "https://fairwaydigitalmedia.com/developer-intent" },
            { label: "Social Intelligence", url: "https://fairwaydigitalmedia.com/social-intelligence" },
            { label: "Data Verification", url: "https://fairwaydigitalmedia.com/data-verification" },
            { label: "Demand Generation", url: "https://fairwaydigitalmedia.com/demand-gen" }
        ]
      },
    ],
  },
];

const PLAYBOOK_DATA = {
  nike: {
    label: "Live Example: NIKE",
    postAuthor: "Retail Tech Daily",
    postRole: "2h • Edited",
    postContent: "Chargeback rates in retail are hitting all-time highs. 🚨 Big box retailers are seeing friendly fraud eat up 15% of margins. Are you using AI to deflect or just eating the cost?",
    likeCount: 428,
    // Tool 2 Data (Message Assist) - SWAPPED TO DAVIDA GINTER
    queue: [
      { name: "Davida Ginter", title: "Co-founder & CEO", company: "Eloo", active: true },
      { name: "Ambruce Carter", title: "Account Manager II", company: "ZoomInfo", active: false },
      { name: "David Adkins", title: "AI & Digital Solutions Leader", company: "Driving Enterprise...", active: false },
      { name: "Juan Salazar", title: "Business Development Specialist", company: "3rd", active: false },
      { name: "Brendan Pitts", title: "Building data-driven GTM", company: "2nd", active: false },
      { name: "DigitalCMO Insight", title: "29 followers", company: "Unknown", active: false },
    ],
    profile: {
      name: "Davida Ginter",
      title: "Co-founder & CEO",
      connection: "Eloo",
      message: "Thanks for connecting, Davida. For Sales Leader, we usually focus on Quota attainment without the fluff. If helpful, I can share a specific example for Eloo."
    },
    persona: {
      type: "Executive",
      level: "C-LEVEL",
      goals: ["Profitability", "Market share"],
      challenges: ["Execution risk", "Time"],
      resources: ["Executive summaries"]
    },
    // Tool 1 Data
    prospects: [
      { name: "Sarah Jenkins", role: "VP, Digital Fraud @ Nike", status: "DECISION_MAKER" },
      { name: "David Chen", role: "Dir. Payments Ops @ Nike", status: "CHAMPION" },
      { name: "Elena Rodriguez", role: "Sr. Risk Analyst @ Nike", status: "USER" },
    ],
  },
  fraud: { label: "Fraud & Risk Leader", likeCount: 215, postAuthor: "Risk Weekly", postRole: "4h", postContent: "New AML regulations...", queue: [], prospects: [] },
  payments: { label: "Payments & Ops Leader", likeCount: 892, postAuthor: "FinTech Insider", postRole: "1d", postContent: "Transaction acceptance rates...", queue: [], prospects: [] }
};

// --- 4. MODALS (Defined before Main App) ---

const MikeRecapEmailModal = ({ onClose }) => (
  <div style={{ ...styles.modalOverlay, zIndex: 9999 }} onClick={onClose}>
    <div style={{ ...styles.modalContent, maxWidth: "700px", background: "#FFFFFF" }} onClick={(e) => e.stopPropagation()}>
      <div style={{ padding: "20px 24px", backgroundColor: "#F8FAFC", borderBottom: `1px solid ${palette.line}` }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "16px" }}>
          <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
            <div style={{ width: "40px", height: "40px", borderRadius: "50%", background: "#0F172A", color: "#FFFFFF", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: "700" }}>MT</div>
            <div>
              <div style={{ fontWeight: "700", color: palette.text }}>Mike Thyne</div>
              <div style={{ fontSize: "12px", color: palette.textSoft }}>VP Customer Success, Fairway Digital Media</div>
            </div>
          </div>
          <div style={{ fontSize: "12px", color: palette.textFaint }}>Wed, Dec 17, 6:47 PM</div>
        </div>
        <div style={{ fontSize: "16px", fontWeight: "600", color: palette.text, marginBottom: "4px" }}>Subject: Thanks Everyone!</div>
      </div>
      <div style={{ padding: "32px", overflowY: "auto", fontSize: "14px", lineHeight: "1.6", color: "#334155" }}>
        <p style={{ marginBottom: "16px" }}>Devlyn and Andrea,</p>
        <p style={{ marginBottom: "16px" }}>Thanks for being available to meet with us again today! Ben and I are glad you were able to make it, Andrea. Anyone hired by Devlyn must be wicked smart (to use the local lingo).</p>
        <p style={{ marginBottom: "16px" }}>Today's call was helpful; it gave us a good sense of your priorities, where you stand with your ICP, etc. Getting approved Target Account Lists for each vertical/solution is a lot of work. But it sounds like you're making progress and will be getting additional input from the field. I feel for you Andrea - I know that effort can be like pulling teeth sometimes 🙂.</p>
        <p style={{ marginBottom: "16px" }}>To steal Devlyn's term, we would love to pull together an ABX Proof of Performance for your review. We'll show you how we find and analyze signals (financial and social), how those translate into actionable Account Plans, and the steps we'd take to educate AEs on how to put them to use. We can also show you how we'd report progress and success and how best to "consume" and communicate important real-time signals. In short, how we'd use all the tools in our arsenal to build something that meets your expectations for quality and speed.</p>
        <p style={{ marginBottom: "16px" }}>I'll check in again after the break to see if you've made a decision on your lead campaign for Q1 (e.g., ATS). Whatever vertical and solution, we're confident we can build something to help you move that old pipeline needle in the right direction.</p>
        <p style={{ marginBottom: "16px" }}>Please let me know if you have any questions! We'll look to set up something in or around the 5th to see where things stand.</p>
        <p style={{ marginTop: "24px" }}>Enjoy the Holidays! Stay warm!<br /><br />All the best,<br /><strong>Mike Thyne</strong></p>
      </div>
      <div style={{ padding: "16px 24px", borderTop: `1px solid ${palette.line}`, backgroundColor: "#F8FAFC", display: "flex", justifyContent: "flex-end" }}>
        <button onClick={onClose} style={{ padding: "8px 20px", backgroundColor: "#FFFFFF", color: palette.text, border: `1px solid ${palette.line}`, borderRadius: "6px", fontWeight: 600, cursor: "pointer", fontSize: "13px" }}>Close Email</button>
      </div>
    </div>
  </div>
);

const DetailModal = ({ activeData, checkboxState, toggleCheckbox, onClose, onMikeEmailClick, onPlaybookClick }) => {
  if (!activeData) return null;
  return (
    <div style={styles.modalOverlay} onClick={onClose}>
      <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <div style={{ padding: "24px", borderBottom: `1px solid ${palette.glassBorder}`, display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <div style={{ fontSize: "13px", color: palette.brandDark, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "8px" }}>Week of {activeData.date}</div>
            <h2 style={{ fontSize: "24px", fontWeight: 700, color: palette.text, margin: 0 }}>{activeData.title}</h2>
          </div>
          <button onClick={onClose} style={{ background: "transparent", border: "none", cursor: "pointer", color: palette.textSoft }}><X size={24} /></button>
        </div>
        <div style={{ padding: "32px", overflowY: "auto", flex: 1 }}>
          <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: "40px" }}>
            <div>
              <h4 style={{ fontSize: "14px", fontWeight: 700, color: palette.text, marginBottom: "12px" }}>Engagement Summary</h4>
              <div style={{ fontSize: "15px", color: palette.textSoft, lineHeight: "1.6", marginBottom: "32px" }} dangerouslySetInnerHTML={{ __html: activeData.summary.replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>") }} />
              <h4 style={{ fontSize: "14px", fontWeight: 700, color: palette.text, marginBottom: "16px" }}>Action Items</h4>
              <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                {activeData.deliverables.map((item) => {
                  const isChecked = checkboxState[item.id];
                  return (
                    <div key={item.id} onClick={() => toggleCheckbox(item.id)} style={{ display: "flex", alignItems: "flex-start", gap: "16px", padding: "16px", borderRadius: "12px", border: isChecked ? `1px solid ${palette.success}` : `1px solid ${palette.line}`, backgroundColor: isChecked ? "#F0FDF4" : "#FFFFFF", cursor: "pointer", transition: "all 0.2s" }}>
                      <div style={{ marginTop: "2px", color: isChecked ? palette.success : palette.textFaint }}>{isChecked ? <CheckCircle2 size={20} /> : <Circle size={20} />}</div>
                      <div>
                        <div style={{ fontSize: "15px", fontWeight: 500, color: isChecked ? palette.success : palette.text, textDecoration: isChecked ? "line-through" : "none" }}>{item.text}</div>
                        <div style={{ fontSize: "12px", color: palette.textSoft, marginTop: "4px" }}>Owner: <span style={{ fontWeight: 600 }}>{item.owner}</span></div>
                        {item.links && (
                          <div style={{ marginTop: "8px", display: "flex", flexWrap: "wrap", gap: "8px" }}>
                            {item.links.map((link, i) => (
                              <span key={i} onClick={(e) => { e.stopPropagation(); if (link.action === "OPEN_MIKE_EMAIL") { e.preventDefault(); onMikeEmailClick(); } else if (link.action === "OPEN_PLAYBOOK") { e.preventDefault(); onPlaybookClick(); } else if (link.url && link.url !== "#") { window.open(link.url, "_blank"); } }} style={{ fontSize: "11px", display: "inline-flex", alignItems: "center", gap: "4px", color: link.action === "OPEN_PLAYBOOK" ? "#FFFFFF" : palette.brandDark, background: link.action === "OPEN_PLAYBOOK" ? palette.text : palette.brandGlow, padding: "4px 10px", borderRadius: "6px", fontWeight: 600, cursor: "pointer", border: link.action === "OPEN_PLAYBOOK" ? "none" : `1px solid ${palette.brand}20`, zIndex: 10, position: "relative" }}>
                                {link.action === "OPEN_MIKE_EMAIL" && <Send size={12} />}
                                {link.action === "OPEN_PLAYBOOK" && <Zap size={12} fill="currentColor" />}
                                {link.label}
                                {!link.action && <ExternalLink size={10} />}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            <div>
              <div style={{ backgroundColor: "#F8FAFC", padding: "20px", borderRadius: "12px" }}>
                <h4 style={{ fontSize: "12px", fontWeight: 700, color: palette.textFaint, textTransform: "uppercase", marginBottom: "12px" }}>Session Details</h4>
                <div style={{ marginBottom: "16px" }}><div style={{ fontSize: "11px", color: palette.textSoft, marginBottom: "4px" }}>Attendees</div><div style={{ fontSize: "13px", fontWeight: 600, color: palette.text }}>{activeData.attendees}</div></div>
                <div><div style={{ fontSize: "11px", color: palette.textSoft, marginBottom: "4px" }}>Focus Areas</div><div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>{activeData.tags.map((t) => <span key={t} style={{ fontSize: "11px", color: palette.text, background: "#FFFFFF", border: `1px solid ${palette.line}`, padding: "2px 6px", borderRadius: "4px" }}>{t}</span>)}</div></div>
              </div>
              {activeData.id === "WK-DEC17" && (
                <div style={{ marginTop: "24px", padding: "20px", border: `1px solid ${palette.brandGlow}`, backgroundColor: "#F0FDF4", borderRadius: "12px" }}>
                  <h4 style={{ fontSize: "13px", fontWeight: 700, color: "#166534", marginBottom: "8px", display: "flex", alignItems: "center", gap: "8px" }}><Target size={14} /> New Opportunity</h4>
                  <p style={{ fontSize: "12px", color: "#166534", lineHeight: "1.5" }}>Proof of Performance is the key. Use the <strong>Social Playbook</strong> tool to demonstrate tangible value to Andrea before Q1.</p>
                </div>
              )}
            </div>
          </div>
        </div>
        <div style={{ padding: "20px 24px", borderTop: `1px solid ${palette.line}`, display: "flex", justifyContent: "flex-end", backgroundColor: palette.bg }}>
          <button onClick={onClose} style={{ padding: "10px 24px", backgroundColor: palette.text, color: "#FFF", border: "none", borderRadius: "8px", fontWeight: 600, cursor: "pointer", fontSize: "14px" }}>Close Details</button>
        </div>
      </div>
    </div>
  );
};

const SocialPlaybookModal = ({ onClose }) => {
  const [activeTool, setActiveTool] = useState("tool1");
  const [selectedPersona, setSelectedPersona] = useState("nike");
  const [tool1Step, setTool1Step] = useState(0); // 0: Post, 1: Popup, 2: Fairway Tool
  const currentData = PLAYBOOK_DATA[selectedPersona];

  return (
    <div style={{ ...styles.modalOverlay, zIndex: 10000 }} onClick={onClose}>
      <div style={{ ...styles.modalContent, maxWidth: "1200px", height: "85vh", background: "#FFFFFF", display: "flex", flexDirection: "column" }} onClick={(e) => e.stopPropagation()}>
        {/* TOP NAV BAR */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 24px", borderBottom: `1px solid ${palette.line}`, backgroundColor: "#F8FAFC" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <div style={{ width: "32px", height: "32px", backgroundColor: palette.brand, borderRadius: "6px", display: "flex", alignItems: "center", justifyContent: "center", color: "#FFFFFF" }}><Zap size={20} fill="currentColor" /></div>
            <h2 style={{ fontSize: "18px", fontWeight: 700, color: palette.text, margin: 0 }}>Social Playbook: ABX Demo</h2>
          </div>
          <div style={{ display: "flex", backgroundColor: "#E2E8F0", padding: "4px", borderRadius: "8px" }}>
            <button onClick={() => setActiveTool("tool1")} style={{ padding: "8px 24px", borderRadius: "6px", border: "none", fontSize: "13px", fontWeight: 600, cursor: "pointer", backgroundColor: activeTool === "tool1" ? "#FFFFFF" : "transparent", color: activeTool === "tool1" ? palette.text : palette.textFaint, boxShadow: activeTool === "tool1" ? "0 1px 2px rgba(0,0,0,0.1)" : "none", transition: "all 0.2s" }}>Tool 1: Reaction Discovery</button>
            <button onClick={() => setActiveTool("tool2")} style={{ padding: "8px 24px", borderRadius: "6px", border: "none", fontSize: "13px", fontWeight: 600, cursor: "pointer", backgroundColor: activeTool === "tool2" ? "#FFFFFF" : "transparent", color: activeTool === "tool2" ? palette.text : palette.textFaint, boxShadow: activeTool === "tool2" ? "0 1px 2px rgba(0,0,0,0.1)" : "none", transition: "all 0.2s" }}>Tool 2: Message Assist</button>
          </div>
          <button onClick={onClose} style={{ background: "transparent", border: "none", cursor: "pointer", color: palette.textFaint }}><X size={24} /></button>
        </div>

        {/* MAIN CONTENT AREA */}
        <div style={{ flex: 1, display: "flex", overflow: "hidden" }}>
          
          {/* TOOL 1 VIEW */}
          {activeTool === 'tool1' && (
            <>
              {/* SIDEBAR */}
              <div style={{ width: "260px", borderRight: `1px solid ${palette.line}`, padding: "24px", backgroundColor: "#FFFFFF", display: "flex", flexDirection: "column", gap: "24px" }}>
                <div>
                  <div style={{ fontSize: "11px", fontWeight: 700, color: palette.textFaint, textTransform: "uppercase", marginBottom: "12px" }}>Choose Your Target</div>
                  <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                    {['fraud', 'payments', 'nike'].map(persona => (
                      <div key={persona} onClick={() => { setSelectedPersona(persona); setTool1Step(0); }} style={{ padding: "10px", borderRadius: "8px", border: selectedPersona === persona ? (persona === 'nike' ? `1px solid ${palette.success}` : `1px solid ${palette.brand}`) : `1px solid ${palette.line}`, backgroundColor: selectedPersona === persona ? (persona === 'nike' ? "#F0FDF4" : palette.brandGlow) : "#FFFFFF", fontSize: "13px", color: selectedPersona === persona ? (persona === 'nike' ? "#166534" : palette.brandDark) : palette.textSoft, cursor: "pointer", fontWeight: selectedPersona === persona ? 600 : 400, transition: "all 0.2s", display: "flex", alignItems: "center", gap: "8px" }}>
                        {persona === 'nike' && <div style={{ width: "8px", height: "8px", borderRadius: "50%", backgroundColor: selectedPersona === 'nike' ? palette.success : palette.textFaint }} />}
                        {persona === 'fraud' ? "Fraud & Risk Leader" : persona === 'payments' ? "Payments & Ops Leader" : "Live Example: NIKE"}
                      </div>
                    ))}
                  </div>
                </div>
                <div style={{ padding: "16px", backgroundColor: "#F8FAFC", borderRadius: "8px", border: `1px solid ${palette.line}`, marginTop: "auto" }}>
                  <div style={{ fontSize: "12px", fontWeight: 700, color: palette.text, marginBottom: "8px" }}>Workflow Context</div>
                  <p style={{ fontSize: "12px", color: palette.textSoft, lineHeight: "1.5" }}>Identify high-intent prospects by mining likes. We use the Fairway Contacts Builder to parse the raw social data into a clean CSV.</p>
                </div>
              </div>

              {/* TOOL 1 INTERFACE */}
              <div style={{ flex: 1, backgroundColor: tool1Step >= 2 ? "#0B1120" : "#F1F5F9", padding: "32px", overflowY: "auto", display: "flex", justifyContent: "center", alignItems: "flex-start", transition: "background-color 0.3s ease" }}>
                <div style={{ maxWidth: tool1Step >= 2 ? "800px" : "640px", width: "100%", backgroundColor: tool1Step >= 2 ? palette.fairwayDark : "#FFFFFF", borderRadius: "12px", boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)", overflow: "hidden", position: "relative", border: tool1Step >= 2 ? `1px solid #1E293B` : "none" }}>
                  {/* STEP 0/1: LINKEDIN */}
                  {tool1Step < 2 && (
                    <>
                      <div style={{ padding: "16px 20px", borderBottom: `1px solid ${palette.line}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <div style={{ fontSize: "14px", fontWeight: 700, color: palette.text }}>Tool 1: Reaction Discovery</div>
                        <Badge text="Simulation Active" tone="success" />
                      </div>
                      <div style={{ padding: "24px" }}>
                        <div style={{ display: "flex", gap: "12px" }}>
                          <div style={{ width: "40px", height: "40px", borderRadius: "50%", backgroundColor: "#E2E8F0", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: "14px", color: palette.text }}>{currentData.postAuthor.substring(0, 2).toUpperCase()}</div>
                          <div>
                            <div style={{ fontSize: "14px", fontWeight: 700 }}>{currentData.postAuthor}</div>
                            <div style={{ fontSize: "12px", color: palette.textFaint }}>{currentData.postRole}</div>
                            <div style={{ marginTop: "12px", fontSize: "14px", lineHeight: "1.5", color: palette.text }}>{currentData.postContent}</div>
                          </div>
                        </div>
                        <div style={{ marginTop: "24px", paddingTop: "12px", borderTop: `1px solid ${palette.line}` }}>
                          <div onClick={() => setTool1Step(1)} style={{ display: "inline-flex", alignItems: "center", gap: "8px", fontSize: "13px", color: palette.textSoft, cursor: "pointer", padding: "8px", borderRadius: "6px", transition: "background 0.2s" }} onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#F1F5F9")} onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}>
                            <span style={{ display: "flex", gap: "2px" }}>👍💡</span><span style={{ fontWeight: 600, color: palette.brandDark, textDecoration: "underline" }}>{currentData.likeCount} (Click to View)</span>
                          </div>
                          <div style={{ display: "flex", justifyContent: "space-between", padding: "12px 32px 0 32px", color: palette.textFaint, fontSize: "13px", fontWeight: 600 }}><span>Like</span><span>Comment</span><span>Repost</span><span>Send</span></div>
                        </div>
                      </div>
                      {tool1Step === 1 && (
                        <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", width: "360px", backgroundColor: "#FFFFFF", borderRadius: "12px", boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.2)", border: `1px solid ${palette.line}`, zIndex: 20, overflow: "hidden" }}>
                          <div style={{ padding: "12px 16px", borderBottom: `1px solid ${palette.line}`, display: "flex", justifyContent: "space-between", alignItems: "center", backgroundColor: "#F8FAFC" }}><span style={{ fontSize: "13px", fontWeight: 700, color: palette.text }}>Reactions ({currentData.likeCount})</span><X size={16} style={{ cursor: "pointer", color: palette.textFaint }} onClick={() => setTool1Step(0)} /></div>
                          <div style={{ maxHeight: "300px", overflowY: "auto", padding: "8px" }}>
                            {currentData.prospects.map((prospect, i) => (
                              <div key={i} style={{ display: "flex", alignItems: "center", gap: "12px", padding: "10px", borderBottom: i < currentData.prospects.length - 1 ? `1px solid ${palette.line}` : "none" }}>
                                <div style={{ width: "32px", height: "32px", borderRadius: "50%", backgroundColor: "#F1F5F9", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "10px", color: palette.textSoft, fontWeight: 700 }}>{prospect.name.charAt(0)}</div>
                                <div>
                                  <div style={{ fontSize: "13px", fontWeight: 600, color: palette.text }}>{prospect.name}</div>
                                  <div style={{ fontSize: "11px", color: palette.textSoft, lineHeight: "1.2" }}>{prospect.role}</div>
                                  <div style={{ display: "flex", alignItems: "center", gap: "4px", fontSize: "10px", fontWeight: 700, color: "#0284C7", marginTop: "2px" }}><Target size={10} /> TARGET PERSONA</div>
                                </div>
                              </div>
                            ))}
                          </div>
                          <div style={{ padding: "12px", backgroundColor: "#F8FAFC", borderTop: `1px solid ${palette.line}`, textAlign: "center" }}>
                            <button onClick={() => setTool1Step(2)} style={{ width: "100%", padding: "8px", backgroundColor: "#0F172A", color: "#FFFFFF", border: "none", borderRadius: "6px", fontWeight: 600, fontSize: "12px", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: "6px" }}><Copy size={12} /> Simulate "Select All & Copy"</button>
                          </div>
                        </div>
                      )}
                    </>
                  )}

                  {/* STEP 2: FAIRWAY BUILDER (DARK MODE) */}
                  {tool1Step >= 2 && (
                    <div style={{ color: '#fff', padding: '24px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
                        <div style={{ fontWeight: 800, fontSize: 16, letterSpacing: '-0.02em' }}>FAIRWAY <span style={{ color: palette.fairwayGreen }}>Digital Media</span></div>
                        <div style={{ height: 20, width: 1, background: '#334155' }}></div>
                        <div style={{ fontSize: 14, color: '#94A3B8' }}>Contacts Builder</div>
                      </div>
                      <div style={{ border: '1px solid #D97706', background: 'rgba(217, 119, 6, 0.1)', borderRadius: 6, padding: 12, fontSize: 11, color: '#FCD34D', marginBottom: 24, lineHeight: 1.4 }}>
                        <AlertTriangle size={12} style={{ display: 'inline', marginRight: 6, marginBottom: -2 }} /><strong>Legal Disclaimer & Privacy Notice:</strong> This tool runs entirely in your browser (Client-Side). No data is sent to external servers.
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 12, marginBottom: 24 }}>{["Employer/Email/Phone verification", "Contact discovery & enrichment", "Role/title parsing", "PII-safe workflows"].map((feat, i) => (<div key={i} style={{ background: '#0F172A', border: '1px solid #1E293B', borderRadius: 6, padding: 10, display: 'flex', gap: 8, alignItems: 'center' }}><CheckCircle2 size={14} color={palette.fairwayGreen} /><span style={{ fontSize: 10, color: '#94A3B8', lineHeight: 1.2 }}>{feat}</span></div>))}</div>
                      <div style={{ display: 'flex', borderBottom: '1px solid #334155', marginBottom: 20 }}>
                        <div style={{ padding: '10px 16px', borderBottom: `2px solid ${palette.fairwayGreen}`, color: palette.fairwayGreen, fontWeight: 600, fontSize: 13 }}>LinkedIn Reactions</div>
                        <div style={{ padding: '10px 16px', color: '#64748B', fontSize: 13, cursor: 'not-allowed' }}>Instagram Lists</div>
                      </div>
                      <div style={{ marginBottom: 20 }}>
                        <div style={{ display: 'flex', gap: 10, marginBottom: 10 }}><Filter size={14} color="#64748B" /><span style={{ fontSize: 11, color: '#64748B', textTransform: 'uppercase', letterSpacing: 1 }}>Reaction Filters</span></div>
                        <div style={{ background: '#0F172A', border: '1px solid #1E293B', borderRadius: 6, padding: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div style={{ fontSize: 13, color: '#fff' }}>like, love, celebrate, insightful, support</div>
                          <div style={{ display: 'flex', gap: 8 }}>
                            {tool1Step === 2 ? (
                              <button onClick={() => setTool1Step(3)} style={{ background: palette.fairwayGreen, border: 'none', color: '#020617', padding: '6px 16px', borderRadius: 4, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>Parse Data</button>
                            ) : (
                              <button onClick={() => setActiveTool("tool2")} style={{ background: '#fff', border: 'none', color: '#020617', padding: '6px 16px', borderRadius: 4, fontSize: 12, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}><Download size={14} /> Download CSV</button>
                            )}
                          </div>
                        </div>
                      </div>
                      <div style={{ height: 200, background: '#020617', border: '1px dashed #334155', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#475569', fontSize: 13 }}>
                        {tool1Step === 2 ? "Paste LinkedIn reaction blocks here..." : (<div style={{ textAlign: 'center' }}><div style={{ color: palette.fairwayGreen, marginBottom: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}><CheckCircle2 size={18} /> Processing Complete</div><div style={{ color: '#94A3B8', fontSize: 12 }}>3 Records Parsed • 3 Emails Found • 3 Roles Matched</div></div>)}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}

          {/* TOOL 2 VIEW (SOCIAL ENGAGE DASHBOARD) */}
          {activeTool === 'tool2' && (
            <div style={{ display: 'flex', flex: 1, backgroundColor: "#F8FAFC", overflow: 'hidden' }}>
              
              {/* LEFT COL: QUEUE */}
              <div style={{ width: '300px', borderRight: `1px solid ${palette.line}`, backgroundColor: '#FFFFFF', display: 'flex', flexDirection: 'column' }}>
                <div style={{ padding: 16, borderBottom: `1px solid ${palette.line}` }}>
                  <div style={{ fontSize: 16, fontWeight: 700, color: palette.text, marginBottom: 12 }}>Queue</div>
                  <div style={{ background: '#F1F5F9', borderRadius: 6, padding: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12, fontWeight: 600 }}>
                    <span style={{ color: palette.textSoft }}>Daily Velocity</span>
                    <span style={{ color: palette.success }}>0 / 50</span>
                  </div>
                  <div style={{ marginTop: 12, position: 'relative' }}>
                    <input type="text" placeholder="Search queue..." style={{ width: '100%', padding: '8px 12px 8px 32px', borderRadius: 6, border: `1px solid ${palette.line}`, fontSize: 12 }} />
                    <SearchIcon size={14} style={{ position: 'absolute', left: 10, top: 10, color: palette.textFaint }} />
                  </div>
                </div>
                <div style={{ flex: 1, overflowY: 'auto' }}>
                  {currentData.queue.map((item, i) => (
                    <div key={i} style={{ padding: 16, borderBottom: `1px solid ${palette.line}`, backgroundColor: item.active ? '#F0F9FF' : 'transparent', borderLeft: item.active ? `3px solid ${palette.brand}` : '3px solid transparent', cursor: 'pointer' }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: palette.text, marginBottom: 4 }}>{item.name}</div>
                      <div style={{ fontSize: 11, color: palette.textSoft, lineHeight: 1.3, height: 28, overflow: 'hidden' }}>{item.title}</div>
                      <div style={{ fontSize: 10, color: palette.textFaint, marginTop: 4 }}>1st • {item.company}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* CENTER COL: ACTION AREA */}
              <div style={{ flex: 1, padding: 32, overflowY: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <div style={{ width: '100%', maxWidth: 600, backgroundColor: '#FFFFFF', borderRadius: 12, border: `1px solid ${palette.line}`, boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)', overflow: 'hidden' }}>
                  {/* Profile Header */}
                  <div style={{ padding: 24, borderBottom: `1px solid ${palette.line}` }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                      <h2 style={{ fontSize: 20, fontWeight: 700, margin: 0 }}>{currentData.profile.name}</h2>
                      <button style={{ fontSize: 11, color: palette.brandDark, background: palette.brandGlow, border: 'none', padding: '4px 8px', borderRadius: 4, fontWeight: 600, cursor: 'pointer' }}>View ↗</button>
                    </div>
                    <div style={{ fontSize: 13, color: palette.textSoft, lineHeight: 1.5, marginBottom: 12 }}>{currentData.profile.title}</div>
                    <div style={{ fontSize: 12, color: palette.textFaint, fontWeight: 600 }}>{currentData.profile.connection}</div>
                    <div style={{ marginTop: 16, display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                      <input type="checkbox" checked readOnly /> <span style={{ color: palette.text }}>Already a 1st Degree Connection?</span>
                    </div>
                  </div>

                  {/* Tabs */}
                  <div style={{ display: 'flex', borderBottom: `1px solid ${palette.line}`, backgroundColor: '#F8FAFC' }}>
                    <div style={{ flex: 1, padding: 12, textAlign: 'center', fontSize: 13, fontWeight: 600, color: palette.brandDark, borderBottom: `2px solid ${palette.brand}`, background: '#FFFFFF' }}>Connect</div>
                    <div style={{ flex: 1, padding: 12, textAlign: 'center', fontSize: 13, color: palette.textSoft }}>Message</div>
                    <div style={{ flex: 1, padding: 12, textAlign: 'center', fontSize: 13, color: palette.textSoft }}>Follow-up</div>
                  </div>

                  {/* Message Area */}
                  <div style={{ padding: 24 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: palette.textFaint, marginBottom: 8, textTransform: 'uppercase' }}>Message Preview</div>
                    <textarea 
                      readOnly 
                      value={currentData.profile.message} 
                      style={{ width: '100%', height: 120, padding: 12, borderRadius: 8, border: `1px solid ${palette.line}`, fontSize: 14, lineHeight: 1.5, color: palette.text, fontFamily: 'sans-serif', resize: 'none' }}
                    />
                    <div style={{ marginTop: 12, fontSize: 12, color: palette.textFaint, display: 'flex', alignItems: 'center', gap: 4 }}>
                      <PlayCircle size={12} /> Adjust Template...
                    </div>
                  </div>

                  {/* Footer Actions */}
                  <div style={{ padding: 24, borderTop: `1px solid ${palette.line}`, display: 'flex', gap: 16 }}>
                    <button style={{ flex: 1, padding: 12, borderRadius: 8, border: `1px solid ${palette.line}`, backgroundColor: '#FFFFFF', color: palette.text, fontWeight: 600, cursor: 'pointer' }}>Skip</button>
                    <button style={{ flex: 2, padding: 12, borderRadius: 8, border: 'none', backgroundColor: '#0F172A', color: '#FFFFFF', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                      <Copy size={16} /> Copy & Open LinkedIn
                    </button>
                  </div>
                </div>
              </div>

              {/* RIGHT COL: PERSONA ENGINE */}
              <div style={{ width: '280px', backgroundColor: '#0F172A', color: '#FFFFFF', padding: 24, overflowY: 'auto' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
                  <div style={{ width: 32, height: 32, backgroundColor: 'rgba(255,255,255,0.1)', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Zap size={18} color={palette.brand} /></div>
                  <div>
                    <div style={{ fontSize: 10, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: 1 }}>Persona • {currentData.persona.level}</div>
                    <div style={{ fontSize: 16, fontWeight: 700 }}>{currentData.persona.type}</div>
                  </div>
                </div>

                <div style={{ marginBottom: 24 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', marginBottom: 12 }}>Top Goals</div>
                  {currentData.persona.goals.map((g, i) => (
                    <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 8, fontSize: 13, color: '#E2E8F0' }}>
                      <Target size={14} color={palette.brand} /> {g}
                    </div>
                  ))}
                </div>

                <div style={{ marginBottom: 24 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', marginBottom: 12 }}>Challenges</div>
                  {currentData.persona.challenges.map((c, i) => (
                    <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 8, fontSize: 13, color: '#E2E8F0' }}>
                      <AlertTriangle size={14} color="#F59E0B" /> {c}
                    </div>
                  ))}
                </div>

                <div style={{ marginBottom: 24 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', marginBottom: 12 }}>Preferred Resources</div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {currentData.persona.resources.map((r, i) => (
                      <span key={i} style={{ fontSize: 11, padding: '4px 8px', borderRadius: 99, backgroundColor: 'rgba(255,255,255,0.1)', color: '#FFFFFF' }}>{r}</span>
                    ))}
                  </div>
                </div>
              </div>

            </div>
          )}

        </div>
      </div>
    </div>
  );
};

// --- 6. MAIN APP LOGIC ---

export default function SeonTracker() {
  const [activeId, setActiveId] = useState(null);
  const [viewingMikeEmail, setViewingMikeEmail] = useState(false);
  const [viewingPlaybook, setViewingPlaybook] = useState(false);
  const [tab, setTab] = useState("tracker");
  const [sidebarOpen, setSidebarOpen] = useState(false); // Mobile sidebar state

  // Mobile detection
  const isMobile = useMediaQuery("(max-width: 768px)");

  // State for checkboxes
  const [checkboxState, setCheckboxState] = useState(() => {
    const initial = {};
    WEEKS_DATA.forEach((week) => {
      week.deliverables.forEach((d) => {
        if (d.defaultChecked) initial[d.id] = true;
      });
    });
    return initial;
  });

  const activeData = useMemo(
    () => WEEKS_DATA.find((d) => d.id === activeId),
    [activeId]
  );

  const toggleCheckbox = (id) => {
    setCheckboxState((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const getStats = (item) => {
    const total = item.deliverables.length;
    const completed = item.deliverables.filter(
      (d) => checkboxState[d.id]
    ).length;
    return { total, completed };
  };

  const Sidebar = () => (
    <>
      {/* Mobile Overlay */}
      {isMobile && sidebarOpen && (
        <div 
          onClick={() => setSidebarOpen(false)}
          style={{
            position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.5)", zIndex: 40
          }}
        />
      )}
      
      <aside style={isMobile ? {
        ...styles.sidebarMobile,
        transform: sidebarOpen ? "translateX(0)" : "translateX(-100%)"
      } : styles.sidebarDesktop}>
        
        {/* Mobile Close Button */}
        {isMobile && (
          <button 
            onClick={() => setSidebarOpen(false)}
            style={{
              position: "absolute", top: 16, right: 16, background: "transparent", border: "none", color: "#FFF"
            }}
          >
            <X size={24} />
          </button>
        )}

        <div style={{ marginBottom: "40px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div
              style={{
                width: 36,
                height: 36,
                background: `linear-gradient(135deg, ${palette.brand} 0%, #00997B 100%)`,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#FFFFFF",
                fontWeight: 700,
                fontSize: 20,
                overflow: "hidden"
              }}
            >
              <img 
                src="https://pub-065b7688176a4969bec21707aed69fdc.r2.dev/SEON/LOGO/Screenshot%202025-12-17%20at%209.11.02%E2%80%AFPM.png" 
                alt="SEON" 
                style={{ width: "100%", height: "100%", objectFit: "cover" }} 
              />
            </div>
            <div
              style={{
                fontSize: 20,
                fontWeight: 700,
                color: "#FFFFFF",
                letterSpacing: "-0.01em",
              }}
            >
              SEON<span style={{ color: palette.brand }}>.IO</span>
            </div>
          </div>
        </div>
        <nav style={{ flex: 1, display: "flex", flexDirection: "column", gap: 8 }}>
          <div style={styles.navItem(tab === "tracker")} onClick={() => { setTab("tracker"); if(isMobile) setSidebarOpen(false); }}>
            <CalendarClock size={18} /> <span>Partnership Tracker</span>
          </div>
          <div style={styles.navItem(tab === "strategy")} onClick={() => { setTab("strategy"); if(isMobile) setSidebarOpen(false); }}>
            <Target size={18} /> <span>ABX Strategy</span>
          </div>
          <div style={styles.navItem(tab === "assets")} onClick={() => { setTab("assets"); if(isMobile) setSidebarOpen(false); }}>
            <Search size={18} /> <span>Signal Intelligence</span>
          </div>
        </nav>
        <div style={{ marginTop: "auto", padding: 16, background: "rgba(255,255,255,0.05)", borderRadius: 12, border: "1px solid rgba(255,255,255,0.1)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Briefcase size={16} color={palette.brand} />
            <span style={{ fontSize: 12, fontWeight: 600, color: "#FFFFFF" }}>Fairway Digital Media</span>
          </div>
        </div>
      </aside>
    </>
  );

  const WeekCard = ({ week }) => {
    const stats = getStats(week);
    return (
      <div
        style={styles.card}
        onClick={() => setActiveId(week.id)}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = "translateY(-2px)";
          e.currentTarget.style.boxShadow = "0 10px 15px -3px rgba(0, 0, 0, 0.1)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = "translateY(0)";
          e.currentTarget.style.boxShadow = "0 1px 3px rgba(0,0,0,0.04)";
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "16px" }}>
          <Badge text={week.date} tone="default" />
          {stats.total === stats.completed && <CheckCircle2 size={18} color={palette.success} />}
        </div>
        <h3 style={{ fontSize: "18px", fontWeight: 700, color: palette.text, marginBottom: "8px", lineHeight: "1.3" }}>{week.title}</h3>
        <p style={{ fontSize: "14px", color: palette.textSoft, marginBottom: "20px", lineHeight: "1.5", height: "42px", overflow: "hidden", textOverflow: "ellipsis", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>{week.summary}</p>
        <div style={{ display: "flex", gap: "6px", flexWrap: "wrap", marginBottom: "16px" }}>
          {week.tags.map((t) => (
            <span key={t} style={{ fontSize: "11px", color: palette.brandDark, background: palette.brandGlow, padding: "2px 8px", borderRadius: "4px", fontWeight: 600 }}>#{t}</span>
          ))}
        </div>
        <div style={{ marginTop: "auto" }}>
          <ProgressBar total={stats.total} completed={stats.completed} />
        </div>
      </div>
    );
  };

  return (
    <div style={styles.container}>
      {/* Mobile Menu Trigger */}
      {isMobile && (
        <button 
          onClick={() => setSidebarOpen(true)}
          style={styles.mobileMenuBtn}
        >
          <Menu size={20} color={palette.text} />
        </button>
      )}

      <Sidebar />
      
      <main
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          padding: isMobile ? "20px" : "32px 40px",
          gap: "24px",
          overflow: "hidden",
          marginTop: isMobile ? "60px" : "0"
        }}
      >
        <section style={styles.glassPanel(isMobile)}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: "32px" }}>
            <div>
              <h1 style={{ fontSize: "24px", fontWeight: 700, color: palette.text, marginBottom: "4px" }}>Engagement Timeline</h1>
              <p style={{ color: palette.textSoft }}>Fairway Digital Media • SEON.io ABX Opportunity</p>
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: "24px" }}>
            {WEEKS_DATA.map((week) => (<WeekCard key={week.id} week={week} />))}
          </div>
        </section>
      </main>

      {activeId && (<DetailModal activeData={activeData} checkboxState={checkboxState} toggleCheckbox={toggleCheckbox} onClose={() => setActiveId(null)} onMikeEmailClick={() => setViewingMikeEmail(true)} onPlaybookClick={() => setViewingPlaybook(true)} />)}
      {viewingMikeEmail && (<MikeRecapEmailModal onClose={() => setViewingMikeEmail(false)} />)}
      {viewingPlaybook && (<SocialPlaybookModal onClose={() => setViewingPlaybook(false)} />)}
    </div>
  );
}
